import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { bulkUploadListings } from '../../util/api';

// ================ Async Thunk ================ //

/**
 * Submit the bulk upload: send the .xlsx file plus the shared location/
 * geolocation to the server endpoint as multipart/form-data. The `geolocation`
 * is a LatLng (from the location autocomplete's selected place) and is
 * transit-serialized by the api helper.
 */
export const submitBulkUploadThunk = createAsyncThunk(
  'BulkUploadListingsPage/submitBulkUpload',
  async ({ file, location, building, geolocation, timezone }, { rejectWithValue }) => {
    try {
      const result = await bulkUploadListings({
        file,
        timezone,
        location: { address: location?.selectedPlace?.address, building: building || '' },
        geolocation,
      });
      return result;
    } catch (e) {
      // The endpoint returns `{ error, details }` on failure; api.js merges that
      // onto the thrown Error, so prefer `e.error` for the message.
      const message = e?.error || e?.message || 'Bulk upload failed.';
      return rejectWithValue({ ...storableError(e), message, details: e?.details });
    }
  }
);

export const submitBulkUpload = actionPayload => dispatch =>
  dispatch(submitBulkUploadThunk(actionPayload));

// ================ Slice ================ //

const bulkUploadListingsPageSlice = createSlice({
  name: 'BulkUploadListingsPage',
  initialState: {
    uploadInProgress: false,
    uploadError: null,
    result: null,
  },
  reducers: {
    clearResult: state => {
      state.result = null;
      state.uploadError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(submitBulkUploadThunk.pending, state => {
        state.uploadInProgress = true;
        state.uploadError = null;
        state.result = null;
      })
      .addCase(submitBulkUploadThunk.fulfilled, (state, action) => {
        state.uploadInProgress = false;
        state.result = action.payload;
      })
      .addCase(submitBulkUploadThunk.rejected, (state, action) => {
        state.uploadInProgress = false;
        state.uploadError = action.payload;
      });
  },
});

export const { clearResult } = bulkUploadListingsPageSlice.actions;
export default bulkUploadListingsPageSlice.reducer;
